import React, { useState } from 'react';
import {
  Grid, Box, Typography, IconButton, Button, MenuItem, TextField
} from '@mui/material';
import Fuse from 'fuse.js';

const categorias = ['TODOS', 'Gobierno Corporativo', 'Familia', 'Empresa', 'Propiedad', 'Empresa Familiar', 'Código de ética', 'Desarrollo de negocios', 'Familia empresaria', 'Mentoring y Coaching', 'Plan estratégico anual', 'Planeación estratégica', 'Responsabilidad corporativa'];

const AllArticle = ({
  articulos,
  searchTerm,
  setSearchTerm,
  filterCategory,
  setFilterCategory,
  sortOrder,
  setSortOrder
}) => {
  const [paginaActual, setPaginaActual] = useState(1);
  const [expandida, setExpandida] = useState(null);
  const articulosPorPagina = 6;

  if (!Array.isArray(articulos) || articulos.length === 0) {
    return (
      <Typography sx={{ color: 'white', textAlign: 'center', mt: 4 }}>
        No hay artículos para mostrar.
      </Typography>
    );
  }

  const opcionesFuse = {
    keys: ['titulo', 'descripcion', 'categoria'],
    threshold: 0.4
  };

  const fuse = new Fuse(articulos, opcionesFuse);

  const resultadosBusqueda = searchTerm
    ? fuse.search(searchTerm).map(resultado => resultado.item)
    : articulos;

  const articulosFiltrados = resultadosBusqueda.filter((articulo) => {
    const coincideCategoria =
      filterCategory === 'TODOS' ||
      (Array.isArray(articulo.categoria)
        ? articulo.categoria.some(cat =>
            cat.toLowerCase() === filterCategory.toLowerCase()
          )
        : articulo.categoria.toLowerCase() === filterCategory.toLowerCase());
    return coincideCategoria;
  });

  const articulosOrdenados = [...articulosFiltrados].sort((a, b) =>
    sortOrder === 'MÁS RECIENTES'
      ? new Date(b.fecha) - new Date(a.fecha)
      : new Date(a.fecha) - new Date(b.fecha)
  );

  const totalPaginas = Math.ceil(articulosOrdenados.length / articulosPorPagina);
  const indiceInicial = (paginaActual - 1) * articulosPorPagina;
  const articulosPaginados = articulosOrdenados.slice(indiceInicial, indiceInicial + articulosPorPagina);

  const cambiarPagina = (nuevaPagina) => {
    setPaginaActual(nuevaPagina);
  };

  const toggleExpand = (index) => {
    setExpandida(expandida === index ? null : index);
  };

  return (
    <Box
      sx={{
        backgroundColor: '#1F1F1F',
        py: 6,
        px: { xs: 2, sm: 4, md: 10 },
        backgroundImage: 'url(https://res.cloudinary.com/dhoyyqszi/image/upload/v1750193681/ebm/5395_wugjdu.png)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Título */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
        <Box
          sx={{
            width: '315px',
            height: '44px',
            borderRadius: '40px',
            background: 'linear-gradient(rgba(10, 98, 116, 0), rgba(10, 98, 116, 0.5))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '40px',
              border: '0.64px solid rgba(54, 176, 176, 1)',
              pointerEvents: 'none',
            },
          }}
        >
          <Typography
            sx={{
              color: '#E6E6E6',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '20px',
              fontWeight: 400,
              textAlign: 'center',
              zIndex: 2,
            }}
          >
            Todos los <Box component="span" sx={{ fontWeight: 800 }}>artículos</Box>
          </Typography>
        </Box>
      </Box>

      {/* Filtros */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 4,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            select
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setPaginaActual(1);
            }}
            size="small"
            sx={{
              minWidth: 160,
              backgroundColor: '#2E2E2E',
              borderRadius: '30px',
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                fontFamily: 'Poppins, sans-serif',
                borderRadius: '30px',
              },
              '& .MuiSelect-icon': { color: '#fff' },
            }}
          >
            {categorias.map((categoria) => (
              <MenuItem key={categoria} value={categoria} sx={{ fontFamily: 'Poppins, sans-serif' }}>
                {categoria}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value);
            }}
            size="small"
            sx={{
              minWidth: 160,
              backgroundColor: '#2E2E2E',
              borderRadius: '30px',
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                fontFamily: 'Poppins, sans-serif',
                borderRadius: '30px',
              },
              '& .MuiSelect-icon': { color: '#fff' },
            }}
          >
            <MenuItem value="MÁS RECIENTES">MÁS RECIENTES</MenuItem>
            <MenuItem value="MÁS ANTIGUOS">MÁS ANTIGUOS</MenuItem>
          </TextField>
        </Box>

        {/* Búsqueda */}
        <Box
          sx={{
            backgroundColor: '#2a2a2a',
            borderRadius: '30px',
            display: 'flex',
            alignItems: 'center',
            padding: '6px 12px',
            width: { xs: '100%', sm: '300px' },
          }}
        >
          <input
            type="text"
            placeholder="Buscar por tema"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPaginaActual(1);
            }}
            style={{
              flex: 1,
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '14px',
            }}
          />
          <Box
            component="img"
            src="https://res.cloudinary.com/dhoyyqszi/image/upload/v1750190221/ebm/iconSearch_oyid1v.svg"
            alt="Buscar"
            sx={{ width: '16px', height: '16px', ml: 1 }}
          />
        </Box>
      </Box>

      {/* Tarjetas */}
      <Grid container spacing={4} justifyContent="center" sx={{ maxWidth: '1000px', margin: '0 auto' }}>
        {articulosPaginados.map((articulo, index) => (
          <Grid item key={index} xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                backgroundColor: '#00088',
                borderRadius: '20px',
                overflow: 'hidden',
                transition: 'transform 0.3s ease',
                width: '100%',
                maxWidth: '296px',
                '&:hover': { transform: 'translateY(-5px)' },
                position: 'relative'
              }}
            >
              <Box
                component="img"
                src={articulo.imagen || '/placeholders/placeholder-articulo.jpg'}
                alt={articulo.titulo}
                sx={{ width: '100%', height: '263px', objectFit: 'cover' }}
              />
              <Box sx={{ p: 2 }}>
                <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '14px', mb: 1 }}>
                  {articulo.titulo}
                </Typography>
                <Typography sx={{ color: '#aaa', fontSize: '13px', mb: 1 }}>
                  {`${articulo.descripcion?.slice(0, 100)}...`}
                </Typography>

                {articulo.enlace?.startsWith('http') ? (
                  <Button
                    component="a"
                    href={articulo.enlace}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#13B8DA', fontSize: '12px', textTransform: 'none', fontWeight: 'bold', p: 0 }}
                  >
                    Leer más
                  </Button>
                ) : (
                  <Button
                    onClick={() => toggleExpand(index)}
                    sx={{ color: '#13B8DA', fontSize: '12px', textTransform: 'none', fontWeight: 'bold', p: 0 }}
                  >
                    Ver más
                  </Button>
                )}

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#aaa' }}>
                  <Typography>{Array.isArray(articulo.categoria) ? articulo.categoria.join(', ') : articulo.categoria}</Typography>
                  <Typography>{new Date(articulo.fecha).toLocaleDateString()}</Typography>
                </Box>
              </Box>

              {expandida === index && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '5%',
                    left: '5%',
                    width: '90%',
                    backgroundColor: '#121212',
                    color: '#fff',
                    zIndex: 10,
                    borderRadius: '10px',
                    padding: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>{articulo.titulo}</Typography>
                  <Typography sx={{ fontSize: '13px' }}>{articulo.descripcion}</Typography>
                  <Box textAlign="right" mt={2}>
                    <Button
                      onClick={() => toggleExpand(index)}
                      sx={{ fontSize: '12px', color: '#13B8DA', textTransform: 'none' }}
                    >
                      Cerrar
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Paginación */}
      <Box sx={{ mt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <IconButton onClick={() => cambiarPagina(Math.max(paginaActual - 1, 1))} disabled={paginaActual === 1} sx={{ color: '#fff' }}>
            <img src="https://res.cloudinary.com/dhoyyqszi/image/upload/v1750190219/ebm/chevron-left_gsgfk7.svg" alt="Anterior" />
          </IconButton>
          <Typography variant="body2" sx={{ fontSize: '16px', fontWeight: 400 }}>
            Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong>
          </Typography>
          <IconButton onClick={() => cambiarPagina(Math.min(paginaActual + 1, totalPaginas))} disabled={paginaActual === totalPaginas} sx={{ color: '#fff' }}>
            <img src="https://res.cloudinary.com/dhoyyqszi/image/upload/v1750190222/ebm/chevron-right_zhuboy.svg" alt="Siguiente" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default AllArticle;
